<script setup lang="ts">
import { computed } from 'vue'

import {
  BetMarketEventKey,
  translateBetMarketKeyToBetMarketType,
  useBetMarketStatus,
} from '@collector/shared-domain'
import { Button, Icon } from '@collector/shared-ui'
import { EventStatusId, IncidentId } from '@collector/sportsapi-client-legacy'
import { useEvent } from '@desktop/views/Relation/composables'
import {
  useBlockades,
  useEventIncidentsQueue,
} from '@desktop/views/Relation/Sports/composables'

interface Props {
  betMarketEventKey: BetMarketEventKey
  betStatusActiveText: string
  betStatusInactiveText: string
  openMarketsIncidentId: IncidentId
  closeMarketsIncidentId: IncidentId
  disabledStatuses: Set<EventStatusId>
}

const props = defineProps<Props>()

// composables
const { event, hasEventCurrentStatus } = useEvent()
const { isBetMarketUpdateEnabled } = useBlockades()
const eventIncidentsQueue = useEventIncidentsQueue()
const { isBetMarketActive: betMarketActive } = useBetMarketStatus(
  event,
  props.betMarketEventKey,
)

// methods
function toggleBetMarket(): void {
  eventIncidentsQueue.addIncident(
    {
      incident_id: betMarketActive.value
        ? props.closeMarketsIncidentId
        : props.openMarketsIncidentId,
    },
    {
      shouldResetPossession: true,
    },
  )
}

const disabled = computed(() => {
  const betMarketType = translateBetMarketKeyToBetMarketType(
    props.betMarketEventKey,
  )

  if (!isBetMarketUpdateEnabled(betMarketType)) {
    return true
  }

  return hasEventCurrentStatus(props.disabledStatuses)
})
</script>

<template>
  <Button
    class="text-neutral-10 disabled:text-neutral-10 px-1.5 text-sm font-bold disabled:border-transparent"
    :class="{
      'enabled:bg-success-2 enabled:border-success-2 enabled:hover:border-success-4 enabled:hover:bg-success-4 disabled:bg-success-4/50':
        betMarketActive,
      'enabled:bg-error-5 enabled:border-error-5 enabled:hover:bg-error-7 enabled:hover:border-error-7 disabled:bg-error-6/50':
        !betMarketActive,
    }"
    size="sm"
    :disabled
    @click="toggleBetMarket"
  >
    <span class="inline">
      <slot v-if="betMarketActive" />
      <Icon
        v-else
        icon="lock"
      />
    </span>

    {{
      betMarketActive ? props.betStatusActiveText : props.betStatusInactiveText
    }}
  </Button>
</template>
